/**
 * Eric BYOK (2026-05-12) — Dashboard Consommation Forfait AI.
 *
 * Permet à chaque admin d'org de suivre :
 *   • Volume consommé (vidéos, minutes transcrites, Q&R, scripts…)
 *   • Estimation coût en CAD répartie par fournisseur (OpenAI, AssemblyAI, OVH)
 *   • Évolution quotidienne (graphique)
 *
 * Les chiffres sont indicatifs — la facture réelle vient des fournisseurs.
 */
import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { toast } from "../components/ui/sonner";
import { useLang } from "../contexts/LanguageContext";
import { Link } from "react-router-dom";
import MeasuredAICosts from "../components/admin/MeasuredAICosts";

import { API_BASE as API } from "../lib/apiBase";

const PERIOD_OPTIONS = [
  { value: "7d", label_fr: "7 derniers jours", label_en: "Last 7 days" },
  { value: "30d", label_fr: "30 derniers jours", label_en: "Last 30 days" },
  { value: "month", label_fr: "Mois en cours", label_en: "Current month" },
  { value: "90d", label_fr: "90 derniers jours", label_en: "Last 90 days" },
  // Vincent #149 (Eric 2026-05-16) — Tendances trimestrielle et annuelle
  { value: "3m", label_fr: "3 derniers mois", label_en: "Last 3 months" },
  { value: "12m", label_fr: "12 derniers mois", label_en: "Last 12 months" },
];

function StatCard({ label, value, hint, color = "blue", testid }) {
  const colorMap = {
    blue: "from-blue-500 to-indigo-600",
    emerald: "from-teal-500 to-teal-600",
    amber: "from-amber-500 to-orange-600",
    rose: "from-rose-500 to-pink-600",
    violet: "from-violet-500 to-purple-600",
    zinc: "from-zinc-500 to-zinc-700",
  };
  return (
    <div
      className="relative rounded-xl border border-zinc-200 bg-white p-4 shadow-sm overflow-hidden"
      data-testid={testid}
    >
      <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${colorMap[color] || colorMap.blue}`} />
      <div className="text-xs font-medium uppercase tracking-wide text-zinc-500">{label}</div>
      <div className="mt-2 text-2xl lg:text-3xl font-bold text-zinc-900" data-testid={`${testid}-value`}>
        {value}
      </div>
      {hint && <div className="mt-1 text-xs text-zinc-500">{hint}</div>}
    </div>
  );
}

function DailyBarChart({ data, lang }) {
  const maxMinutes = useMemo(
    () => Math.max(1, ...data.map((d) => d.minutes || 0)),
    [data]
  );
  if (!data || data.length === 0) {
    return (
      <div className="text-center py-12 text-sm text-zinc-500" data-testid="consumption-empty-chart">
        {lang === "fr"
          ? "Aucune activité de transcription sur cette période."
          : "No transcription activity in this period."}
      </div>
    );
  }
  return (
    <div className="space-y-2" data-testid="consumption-daily-chart">
      <div className="flex items-end gap-1 h-40 overflow-x-auto pb-2">
        {data.map((d) => {
          const heightPct = Math.max(2, (d.minutes / maxMinutes) * 100);
          return (
            <div
              key={d.date}
              className="flex-1 min-w-[12px] h-full group relative flex flex-col justify-end"
              data-testid={`consumption-day-${d.date}`}
            >
              <div
                className="bg-gradient-to-t from-[#002FA7] to-[#4d6ff1] rounded-t hover:from-[#001F7A] transition-all cursor-pointer"
                style={{ height: `${heightPct}%` }}
              />
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-zinc-900 text-white text-[10px] rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
                {d.date} · {d.minutes} min · {d.videos} vidéo{d.videos > 1 ? "s" : ""}
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex justify-between text-[10px] text-zinc-400 px-1">
        <span>{data[0]?.date}</span>
        <span>{data[data.length - 1]?.date}</span>
      </div>
    </div>
  );
}

function BudgetProgress({ budget, currentCad, lang }) {
  const pct = Math.min(150, budget.percent_used || 0); // cap visuel à 150%
  const barWidth = Math.min(100, pct);
  const statusMap = {
    ok: { color: "bg-teal-500", text: lang === "fr" ? "Sous budget" : "Under budget", textColor: "text-teal-700", bg: "bg-teal-50", border: "border-teal-200" },
    warning: { color: "bg-amber-500", text: lang === "fr" ? "Attention — 75%" : "Warning — 75%", textColor: "text-amber-700", bg: "bg-amber-50", border: "border-amber-200" },
    critical: { color: "bg-orange-500", text: lang === "fr" ? "Critique — 90%" : "Critical — 90%", textColor: "text-orange-700", bg: "bg-orange-50", border: "border-orange-200" },
    exceeded: { color: "bg-rose-600", text: lang === "fr" ? "Dépassé" : "Exceeded", textColor: "text-rose-700", bg: "bg-rose-50", border: "border-rose-200" },
  };
  const meta = statusMap[budget.status] || statusMap.ok;
  return (
    <div className={`mb-6 border ${meta.border} ${meta.bg} rounded-xl p-5`} data-testid="consumption-budget-progress">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <div>
          <div className="text-xs uppercase tracking-wide text-zinc-500 font-semibold mb-1">
            {lang === "fr" ? "Budget mensuel" : "Monthly budget"}
          </div>
          <div className="text-xl font-bold text-zinc-900">
            {currentCad.toFixed(2)} $ <span className="text-sm text-zinc-500 font-normal">/ {budget.monthly_cad.toFixed(2)} CAD$</span>
          </div>
        </div>
        <div className="text-right">
          <span className={`px-3 py-1 rounded-full text-xs font-bold ${meta.textColor} ${meta.bg} border ${meta.border}`} data-testid="consumption-budget-status">
            {meta.text}
          </span>
          <div className="text-2xl font-bold text-zinc-900 mt-1" data-testid="consumption-budget-percent">
            {budget.percent_used}%
          </div>
        </div>
      </div>
      <div className="relative w-full h-4 bg-white border border-zinc-200 rounded-full overflow-hidden">
        <div
          className={`absolute top-0 left-0 h-full transition-all ${meta.color}`}
          style={{ width: `${barWidth}%` }}
          data-testid="consumption-budget-bar"
        />
        {/* Marqueurs 75% / 90% */}
        <div className="absolute top-0 h-full w-px bg-zinc-400/50" style={{ left: "75%" }} />
        <div className="absolute top-0 h-full w-px bg-zinc-400/70" style={{ left: "90%" }} />
      </div>
      <div className="flex justify-between text-[10px] text-zinc-500 mt-1">
        <span>0 $</span>
        <span>75%</span>
        <span>90%</span>
        <span>{budget.monthly_cad.toFixed(0)} $</span>
      </div>
      {budget.status === "exceeded" && (
        <p className="text-xs text-rose-700 mt-3 font-medium">
          🚨 {lang === "fr"
            ? `Budget dépassé de ${(currentCad - budget.monthly_cad).toFixed(2)} CAD$. Considérez ajuster vos Hard Limits chez OpenAI / AssemblyAI ou augmenter votre budget cible.`
            : `Budget exceeded by ${(currentCad - budget.monthly_cad).toFixed(2)} CAD$. Consider adjusting your Hard Limits at OpenAI / AssemblyAI or increasing your target budget.`}
        </p>
      )}
    </div>
  );
}

function ServiceCostRow({ service, info, lang }) {
  const SERVICE_META = {
    openai: { icon: "🤖", label: "OpenAI", sub: "Whisper + GPT-5.2" },
    assemblyai: { icon: "🎙️", label: "AssemblyAI", sub: lang === "fr" ? "Diarisation locuteurs" : "Speaker diarisation" },
    ovh_storage: { icon: "🍁", label: "OVHcloud Beauharnois", sub: lang === "fr" ? "Stockage Québec" : "Québec storage" },
  };
  const meta = SERVICE_META[service] || { icon: "🔌", label: service, sub: "" };
  return (
    <div
      className={`flex items-center gap-3 p-4 rounded-lg border ${
        info.configured ? "border-zinc-200 bg-white" : "border-amber-200 bg-amber-50"
      }`}
      data-testid={`consumption-service-${service}`}
    >
      <div className="text-2xl flex-shrink-0">{meta.icon}</div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="font-semibold text-zinc-900 text-sm">{meta.label}</div>
          {!info.configured && (
            <span className="px-1.5 py-0.5 text-[10px] font-bold rounded bg-amber-200 text-amber-900">
              {lang === "fr" ? "Clé manquante" : "Key missing"}
            </span>
          )}
        </div>
        <div className="text-xs text-zinc-500">{meta.sub}</div>
        {service === "openai" && info.details && (
          <div className="text-[11px] text-zinc-500 mt-1">
            Whisper : {info.details.whisper_minutes} min ({info.details.whisper_cad}$) · GPT : {info.details.gpt_units} unités ({info.details.gpt_cad}$)
          </div>
        )}
        {service === "assemblyai" && info.details && (
          <div className="text-[11px] text-zinc-500 mt-1">
            {info.details.diarisation_minutes} {lang === "fr" ? "min de diarisation" : "min diarised"}
          </div>
        )}
        {service === "ovh_storage" && info.details && (
          <div className="text-[11px] text-zinc-500 mt-1">
            {info.details.storage_gb} GB {lang === "fr" ? "stockés (récurrent mensuel)" : "stored (monthly recurring)"}
          </div>
        )}
      </div>
      <div className="text-right">
        <div className="text-lg font-bold text-zinc-900" data-testid={`consumption-cost-${service}`}>
          {info.amount_cad.toFixed(2)} $
        </div>
        <div className="text-[10px] text-zinc-500">CAD</div>
      </div>
    </div>
  );
}

export default function AIPackageConsumptionPage() {
  const { lang } = useLang();
  const [period, setPeriod] = useState("30d");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async (p) => {
    setLoading(true);
    try {
      const { data: resp } = await axios.get(`${API}/api/org/ai-package/consumption`, {
        params: { period: p },
        withCredentials: true,
      });
      setData(resp);
    } catch (e) {
      toast.error(e.response?.data?.detail || (lang === "fr" ? "Impossible de charger la consommation" : "Failed to load consumption"));
    } finally {
      setLoading(false);
    }
  };

  const [exporting, setExporting] = useState(null); // 'csv' | 'pdf' | null
  const exportFile = async (format) => {
    if (exporting) return;
    setExporting(format);
    try {
      const resp = await axios.get(`${API}/api/org/ai-package/consumption/export`, {
        params: { period, format },
        withCredentials: true,
        responseType: "blob",
      });
      // Récupère le nom de fichier depuis Content-Disposition si présent
      const disp = resp.headers["content-disposition"] || "";
      const match = disp.match(/filename="?([^";]+)"?/i);
      const filename = match ? match[1] : `knoria-consommation-${period}.${format}`;
      const blob = new Blob([resp.data], {
        type: format === "csv" ? "text/csv;charset=utf-8" : "application/pdf",
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(lang === "fr" ? `Export ${format.toUpperCase()} téléchargé` : `${format.toUpperCase()} export downloaded`);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || (lang === "fr" ? "Échec de l'export" : "Export failed"));
    } finally {
      setExporting(null);
    }
  };

  useEffect(() => {
    load(period);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period]);

  if (loading && !data) {
    return (
      <div className="p-6">
        <p className="text-zinc-500 mt-4">{lang === "fr" ? "Chargement..." : "Loading..."}</p>
      </div>
    );
  }

  if (!data) return null;

  const { totals, cost_estimate_cad, daily_series } = data;

  return (
    <div className="p-4 lg:p-6 max-w-6xl" data-testid="ai-package-consumption-page">
      <div className="mt-4 mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-zinc-900">
            📊 {lang === "fr" ? "Consommation Forfait AI" : "AI Package Consumption"}
          </h1>
          <p className="text-sm text-zinc-600 mt-2 max-w-2xl">
            {lang === "fr"
              ? "Volume consommé et estimation des coûts facturés directement par OpenAI, AssemblyAI et OVHcloud à votre organisation."
              : "Volume consumed and estimated costs billed directly by OpenAI, AssemblyAI and OVHcloud to your organisation."}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => exportFile("csv")}
            disabled={exporting !== null}
            className="px-3 py-2 rounded-lg border border-teal-300 bg-white hover:bg-teal-50 text-teal-700 text-xs font-medium disabled:opacity-50"
            data-testid="consumption-export-csv-btn"
          >
            {exporting === "csv" ? (lang === "fr" ? "Export..." : "Exporting...") : `⬇ CSV`}
          </button>
          <button
            onClick={() => exportFile("pdf")}
            disabled={exporting !== null}
            className="px-3 py-2 rounded-lg border border-rose-300 bg-white hover:bg-rose-50 text-rose-700 text-xs font-medium disabled:opacity-50"
            data-testid="consumption-export-pdf-btn"
          >
            {exporting === "pdf" ? (lang === "fr" ? "Export..." : "Exporting...") : `⬇ PDF`}
          </button>
          <Link
            to="/dashboard/settings/ai-package"
            className="px-3 py-2 rounded-lg border border-blue-300 bg-white hover:bg-blue-50 text-blue-700 text-xs font-medium"
            data-testid="consumption-manage-keys-btn"
          >
            ⚙️ {lang === "fr" ? "Gérer mes clés" : "Manage keys"}
          </Link>
        </div>
      </div>

      {/* Period selector */}
      <div className="mb-6 flex flex-wrap gap-2" data-testid="consumption-period-selector">
        {PERIOD_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            onClick={() => setPeriod(opt.value)}
            className={`px-3 py-1.5 text-xs rounded-full font-medium transition-all ${
              period === opt.value
                ? "bg-[#002FA7] text-white shadow"
                : "bg-white border border-zinc-300 text-zinc-700 hover:border-blue-300 hover:text-blue-700"
            }`}
            data-testid={`consumption-period-${opt.value}`}
          >
            {lang === "fr" ? opt.label_fr : opt.label_en}
          </button>
        ))}
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <StatCard
          label={lang === "fr" ? "Vidéos uploadées" : "Videos uploaded"}
          value={totals.videos_uploaded}
          color="blue"
          testid="consumption-stat-videos"
        />
        <StatCard
          label={lang === "fr" ? "Minutes transcrites" : "Minutes transcribed"}
          value={totals.transcript_minutes.toLocaleString("fr-CA")}
          hint={`${totals.transcripts_count} ${lang === "fr" ? "transcripts" : "transcripts"}`}
          color="emerald"
          testid="consumption-stat-minutes"
        />
        <StatCard
          label={lang === "fr" ? "Connaissances générées" : "Knowledge generated"}
          value={totals.knowledge_total}
          hint={`${totals.qa_units} Q&R · ${totals.procedures} ${lang === "fr" ? "procédures" : "procedures"} · ${totals.scripts} scripts`}
          color="violet"
          testid="consumption-stat-knowledge"
        />
        <StatCard
          label={lang === "fr" ? "Coût estimé" : "Estimated cost"}
          value={`${cost_estimate_cad.total.toFixed(2)} $`}
          hint={`CAD · ${cost_estimate_cad.currency}`}
          color="amber"
          testid="consumption-stat-cost"
        />
      </div>

      {/* Budget progress bar (si configuré) */}
      {data.budget && (
        <BudgetProgress budget={data.budget} currentCad={cost_estimate_cad.total} lang={lang} />
      )}

      {/* Chart */}
      <div className="bg-white border border-zinc-200 rounded-xl p-5 mb-6">
        <h2 className="text-sm font-semibold text-zinc-900 mb-4">
          📈 {lang === "fr" ? "Activité quotidienne (minutes transcrites)" : "Daily activity (minutes transcribed)"}
        </h2>
        <DailyBarChart data={daily_series} lang={lang} />
      </div>

      {/* Cost breakdown */}
      <div className="bg-white border border-zinc-200 rounded-xl p-5 mb-6">
        <h2 className="text-sm font-semibold text-zinc-900 mb-3">
          💰 {lang === "fr" ? "Répartition par fournisseur" : "Breakdown by provider"}
        </h2>
        <div className="space-y-2">
          {Object.entries(cost_estimate_cad.by_service).map(([service, info]) => (
            <ServiceCostRow key={service} service={service} info={info} lang={lang} />
          ))}
        </div>
        <div className="mt-4 px-3 py-2 rounded bg-zinc-50 border border-zinc-200 text-xs text-zinc-600">
          ℹ️ {cost_estimate_cad.disclaimer}
          <br />
          <span className="text-[10px] text-zinc-400">
            {lang === "fr" ? "Taux de change appliqué" : "FX rate applied"} : 1 USD = {cost_estimate_cad.fx_rate_usd_cad} CAD
          </span>
        </div>
      </div>

      {/* B1124 (Eric 2026-09-08) — Au-dessus, l'estimation par moyenne
          (volumes × tarif type). Ici, la consommation MESURÉE appel par appel :
          les deux cohabitent volontairement, pour qu'on voie l'écart. */}
      <MeasuredAICosts />
    </div>
  );
}
